import {
  borderRadius,
  breakpoints,
  customScrollMinimal,
  Dropdown,
  DropdownList,
  DropdownListOption,
  DropdownProps,
  Input,
  typographyV2,
} from '@toggle/design-system';
import styled, { css } from 'styled-components';

const INPUT_BORDER_RADIUS = '24px';
const INPUT_BORDER_RADIUS_IN_HEADER = '18px';

export const Overlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  z-index: 1;
  background: var(--background-backdrop);

  @media ${breakpoints.lg} {
    display: block;
  }
`;

export const ResultsPlaceholder = styled.p<{
  noResults: boolean;
  $isHeaderSearch: boolean;
}>`
  ${typographyV2.bodySm};
  padding: 8px 12px;
  background-color: var(--surface-default);
  margin: 12px 16px 0px 16px;
  ${borderRadius.r3};
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;

  ${props =>
    props.noResults
      ? `
          color: var(--text-default);
          flex-grow: 1;
        `
      : 'color:var(--text-soft)'};

  @media ${breakpoints.lg} {
    margin-top: 0px;
    margin: 0px;
    border-top-right-radius: 0px;
    border-top-left-radius: 0px;
  }
`;

interface Props {
  $isHeaderSearch: boolean;
  $isChipsVisible: boolean;
  shouldShowItems: boolean;
}

export const StyledDropdown = styled(Dropdown)<Props>`
  width: 100%;
  margin-bottom: auto;
  padding-top: 0px;

  ${DropdownListOption.Styled.ListOption} {
    position: relative;
    height: 52px;
    line-height: 52px;
    padding: 0px 8px;
    border: 1px solid transparent;
    border-radius: 4px;
    background-color: var(--surface-default);
    margin: 4px 8px 4px;

    &.active {
      background-color: var(--surface-hover);
      &::before {
        content: '';
        position: absolute;
        left: -8px;
        width: 4px;
        height: 100%;
        background-color: var(--surface-primary);
        border-radius: 0px 2px 4px 0px;
      }
    }

    &:hover:not(.active) {
      background-color: var(--surface-hover);
    }

    &:not(.active).focused,
    &.focused {
      &::after {
        content: '';
        position: absolute;
        top: 0px;
        left: -8px;
        width: calc(100% + 16px);
        height: 100%;
        border-radius: 4px;
        border: 1px solid var(--border-focus);
      }
    }
  }

  ${props =>
    props.$isHeaderSearch &&
    css`
      @media ${breakpoints.lg} {
        width: 554px;
        z-index: 2;

        ${Input.Styles.Container} {
          z-index: inherit;
        }
      }
    `}

  ${Input.Styles.InputElement} {
    background: none;
    width: 100%;

    &:focus::placeholder {
      visibility: hidden;
    }
  }

  ${Input.Styles.InputWrapper} {
    padding: 0 12px;
    height: ${props => (props.$isHeaderSearch ? '36px' : '44px')};
    border: 1px solid transparent;
    border-radius: ${props =>
      props.$isHeaderSearch
        ? INPUT_BORDER_RADIUS_IN_HEADER
        : INPUT_BORDER_RADIUS};

    svg {
      fill: var(--text-soft);
    }
  }

  @media screen and (max-width: 1040px) {
    display: none;

    ${Input.Styles.Container} {
      pointer-events: none;
    }

    ${({ shouldShowItems }) =>
      shouldShowItems &&
      css`
        display: flex;
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 2;
        background: var(--background-default);

        ${Dropdown.Styled.DropdownListContainer} {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          overflow-y: auto;
          ${customScrollMinimal}
        }

        ${Input.Styles.Container} {
          pointer-events: auto;
        }

        ${DropdownList.Styled.DropdownListRoot} {
          display: flex;
          flex-direction: column;
          top: 69px;
          margin: 0px 16px 16px;
          background: var(--surface-default);
          ${borderRadius.r3};
          flex-grow: 1;
        }

        ${DropdownList.Styled.DropdownListRoot} {
          position: static;
          width: auto;
          border: none;
          max-height: unset;
          padding: 0;
          overflow-y: initial;
        }

        ${ResultsPlaceholder} +  ${DropdownList.Styled.DropdownListRoot} {
          border-top-left-radius: 0px;
          border-top-right-radius: 0px;
        }

        ${Input.Styles.Container} {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-default);
        }

        ${Input.Styles.InputWrapper} {
          border: none;
          height: 44px;
        }
      `}
  }

  @media ${breakpoints.lg} {
    display: flex;
    ${({ shouldShowItems, $isHeaderSearch }) =>
      shouldShowItems
        ? css`
            ${Dropdown.Styled.DropdownListContainer} {
              background-color: var(--surface-default);
              border: 1px solid var(--border-soft);
              border-radius: ${$isHeaderSearch
                ? INPUT_BORDER_RADIUS_IN_HEADER
                : INPUT_BORDER_RADIUS};
              border-top-right-radius: 0px;
              border-top-left-radius: 0px;
            }

            ${Input.Styles.InputWrapper} {
              border-bottom-right-radius: 0px;
              border-bottom-left-radius: 0px;
              border: 1px solid var(--border-soft);
              border-bottom: none;
            }
            ${DropdownList.Styled.DropdownListRoot} {
              display: block;
              border: none;
              padding: 0px;
              position: relative;
              overflow-y: auto;
              ${customScrollMinimal}
              max-height: 352px;
              z-index: inherit;
            }
          `
        : css`
            ${DropdownList.Styled.DropdownListRoot} {
              display: none;
            }
          `}

    ${({ shouldShowItems, $isChipsVisible }) =>
      shouldShowItems &&
      css`
        ${Dropdown.Styled.DropdownListContainer} {
          padding-top: ${$isChipsVisible ? '56px' : '0px'};
          position: absolute;
          top: 100%;
          width: 100%;
          overflow: hidden;
        }
      `}
  }

  @media ${breakpoints.lg} and (max-height: 880px) {
    ${DropdownList.Styled.DropdownListRoot} {
      max-height: ${props => props.shouldShowItems && '230px'};
    }
  }
` as <T>(props: DropdownProps<T> & Props) => JSX.Element;

export const Hotkey = styled.div`
  background-color: var(--surface-hover);
  ${borderRadius.r2};
  padding: 4px;
  color: var(--text-soft);
  ${typographyV2.bodyXs}
`;
